import React, { useState, useEffect } from 'react';
import { 
  CubeIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface InventoryItem {
  id: string;
  name: string;
  category: 'queens' | 'nucs' | 'honey' | 'equipment' | 'bees' | 'supplies';
  currentStock: number;
  totalCapacity: number;
  status: 'in_stock' | 'low_stock' | 'out_of_stock' | 'pre_order';
  price: number;
  unit: string;
  lastUpdated: string;
  description?: string;
  estimatedRestock?: string;
}

interface InventoryTrackerProps {
  className?: string;
  sellerId?: string;
  maxItems?: number;
  showAddButton?: boolean;
  compact?: boolean;
}

const InventoryTracker: React.FC<InventoryTrackerProps> = ({
  className = '',
  maxItems = 6,
  showAddButton = false,
  compact = false
}) => {
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  // Mock inventory data
  const mockInventory: InventoryItem[] = [
    {
      id: '1',
      name: 'Italian Queen Bees',
      category: 'queens',
      currentStock: 12,
      totalCapacity: 50,
      status: 'in_stock',
      price: 35.00,
      unit: 'each',
      lastUpdated: '2025-08-05T10:30:00Z',
      description: 'High-quality Italian queens, marked and mated',
      estimatedRestock: '2025-08-15'
    },
    {
      id: '2',
      name: '5-Frame Nucs',
      category: 'nucs',
      currentStock: 3,
      totalCapacity: 20,
      status: 'low_stock',
      price: 180.00,
      unit: 'each',
      lastUpdated: '2025-08-05T09:15:00Z',
      description: 'Strong 5-frame nucleus colonies with queen',
      estimatedRestock: '2025-08-20'
    },
    {
      id: '3',
      name: 'Wildflower Honey',
      category: 'honey',
      currentStock: 24,
      totalCapacity: 100,
      status: 'in_stock',
      price: 12.00,
      unit: 'lb',
      lastUpdated: '2025-08-05T08:45:00Z',
      description: 'Raw, unfiltered wildflower honey'
    },
    {
      id: '4',
      name: 'Deep Hive Bodies',
      category: 'equipment',
      currentStock: 0,
      totalCapacity: 30,
      status: 'out_of_stock',
      price: 25.00,
      unit: 'each',
      lastUpdated: '2025-08-04T16:20:00Z',
      description: '10-frame deep hive bodies, assembled',
      estimatedRestock: '2025-08-25'
    },
    {
      id: '5',
      name: 'Package Bees (3 lbs)',
      category: 'bees',
      currentStock: 0,
      totalCapacity: 15,
      status: 'pre_order',
      price: 150.00,
      unit: 'package',
      lastUpdated: '2025-08-05T11:00:00Z',
      description: '3 lb packages with queen, available for pre-order',
      estimatedRestock: '2025-09-01'
    },
    {
      id: '6',
      name: 'Bee Smoker',
      category: 'supplies',
      currentStock: 8,
      totalCapacity: 25,
      status: 'in_stock',
      price: 28.00,
      unit: 'each',
      lastUpdated: '2025-08-05T07:30:00Z',
      description: 'Professional-grade bee smoker with leather bellows'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setInventoryItems(mockInventory);
      setLoading(false);
    }, 1000);
  }, []);

  const categories = [
    { value: 'all', label: 'All Items' },
    { value: 'queens', label: 'Queens' },
    { value: 'nucs', label: 'Nucs' },
    { value: 'honey', label: 'Honey' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'bees', label: 'Bees' },
    { value: 'supplies', label: 'Supplies' }
  ];

  const filteredItems = inventoryItems.filter(item => 
    filter === 'all' || item.category === filter
  ).slice(0, maxItems);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'in_stock':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'low_stock':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'out_of_stock':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'pre_order':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <CubeIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in_stock':
        return 'In Stock';
      case 'low_stock':
        return 'Low Stock';
      case 'out_of_stock':
        return 'Out of Stock';
      case 'pre_order':
        return 'Pre-Order';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_stock':
        return 'bg-green-100 text-green-800';
      case 'low_stock':
        return 'bg-yellow-100 text-yellow-800';
      case 'out_of_stock':
        return 'bg-red-100 text-red-800';
      case 'pre_order':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockPercentage = (current: number, total: number) => {
    return total > 0 ? Math.round((current / total) * 100) : 0;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`inventory-tracker ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const InventoryItem: React.FC<{ item: InventoryItem }> = ({ item }) => {
    const stockPercentage = getStockPercentage(item.currentStock, item.totalCapacity);
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
            <p className="text-sm text-gray-600 capitalize">{item.category}</p>
          </div>
          
          <div className="flex items-center space-x-2">
            {getStatusIcon(item.status)}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
              {getStatusText(item.status)}
            </span>
          </div>
        </div>
        
        {!compact && item.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
        )}
        
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm">
            <span className="font-medium text-gray-900">
              {item.currentStock} / {item.totalCapacity}
            </span>
            <span className="text-gray-500 ml-1">{item.unit}</span>
          </div>
          
          <div className="text-right">
            <div className="font-semibold text-gray-900">
              {formatPrice(item.price)}
            </div>
            <div className="text-xs text-gray-500">
              per {item.unit}
            </div>
          </div>
        </div>
        
        {/* Stock Level Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Stock Level</span>
            <span>{stockPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                stockPercentage > 50 ? 'bg-green-500' :
                stockPercentage > 20 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${stockPercentage}%` }}
            ></div>
          </div>
        </div>
        
        <div className="flex justify-between items-center text-xs text-gray-500">
          <span>Updated: {formatDate(item.lastUpdated)}</span>
          {item.estimatedRestock && item.status !== 'in_stock' && (
            <span>Restock: {new Date(item.estimatedRestock).toLocaleDateString()}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`inventory-tracker ${className}`}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
            <CubeIcon className="h-6 w-6 mr-2" />
            Inventory Tracker
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Monitor your stock levels and availability
          </p>
        </div>
        
        {showAddButton && (
          <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Item
          </button>
        )}
      </div>
      
      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6 overflow-x-auto">
        {categories.map(category => (
          <button
            key={category.value}
            onClick={() => setFilter(category.value)}
            className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              filter === category.value
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>
      
      {/* Inventory Items */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <CubeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No items found</h4>
          <p className="text-gray-600">
            {filter === 'all' ? 'No inventory items available.' : `No ${filter} items found.`}
          </p>
        </div>
      ) : (
        <div className={`grid gap-4 ${
          compact 
            ? 'grid-cols-1 md:grid-cols-2' 
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {filteredItems.map(item => (
            <InventoryItem key={item.id} item={item} />
          ))}
        </div>
      )}
      
      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {inventoryItems.filter(item => item.status === 'in_stock').length}
          </div>
          <div className="text-sm text-green-700">In Stock</div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">
            {inventoryItems.filter(item => item.status === 'low_stock').length}
          </div>
          <div className="text-sm text-yellow-700">Low Stock</div>
        </div>
        
        <div className="bg-red-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">
            {inventoryItems.filter(item => item.status === 'out_of_stock').length}
          </div>
          <div className="text-sm text-red-700">Out of Stock</div>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {inventoryItems.filter(item => item.status === 'pre_order').length}
          </div>
          <div className="text-sm text-blue-700">Pre-Order</div>
        </div>
      </div>
    </div>
  );
};

export default InventoryTracker;
